/**
 * Operations Dashboard widget registry — canonical IDs for desktop grid +
 * mobile carousel ordering. Carousel screens and legacy KPI cards share one list.
 */

import { DASHBOARD_CAROUSEL_ENTRIES } from "./dashboardCarousel";

export const DASHBOARD_WIDGET_KIND = Object.freeze({
  CAROUSEL: "carousel",
  KPI: "kpi",
});

/** Legacy KPI cards hosted on the kpi_dashboard page (not separate swipe screens). */
export const DASHBOARD_KPI_WIDGETS = Object.freeze([
  {
    id: "weekly_snapshot",
    kind: DASHBOARD_WIDGET_KIND.KPI,
    screenId: "kpi_dashboard",
    label: "Weekly Snapshot",
    colSpan: 1,
  },
  {
    id: "crew_snapshot",
    kind: DASHBOARD_WIDGET_KIND.KPI,
    screenId: "kpi_dashboard",
    label: "Crew Snapshot",
    colSpan: 1,
  },
  {
    id: "project_hours",
    kind: DASHBOARD_WIDGET_KIND.KPI,
    screenId: "kpi_dashboard",
    label: "Project Hours",
    colSpan: 1,
  },
  {
    id: "recent_activity",
    kind: DASHBOARD_WIDGET_KIND.KPI,
    screenId: "kpi_dashboard",
    label: "Recent Activity",
    colSpan: 1,
  },
]);

const carouselWidgets = DASHBOARD_CAROUSEL_ENTRIES.filter(
  (entry) => entry.id !== "kpi_dashboard",
).map((entry) => ({
  id: entry.id,
  kind: DASHBOARD_WIDGET_KIND.CAROUSEL,
  screenId: entry.id,
  label: entry.label,
  colSpan: 1,
}));

/**
 * Default desktop order: operational summaries, then legacy KPI cards.
 * (Matches prior layout: ops workspace above KPI metrics.)
 */
export const DASHBOARD_WIDGET_REGISTRY = Object.freeze([
  ...carouselWidgets,
  ...DASHBOARD_KPI_WIDGETS,
]);

export const DEFAULT_DASHBOARD_WIDGET_ORDER = Object.freeze(
  DASHBOARD_WIDGET_REGISTRY.map((widget) => widget.id),
);

export const DASHBOARD_WIDGET_BY_ID = Object.freeze(
  Object.fromEntries(
    DASHBOARD_WIDGET_REGISTRY.map((widget) => [widget.id, widget]),
  ),
);

export function getDashboardWidget(widgetId) {
  return DASHBOARD_WIDGET_BY_ID[widgetId] || null;
}

/**
 * Merge a saved order with the registry: keep known ids in saved sequence,
 * append any new registry ids, drop unknowns.
 */
export function resolveDashboardWidgetOrder(
  savedOrder,
  { availableIds = null } = {},
) {
  const allowed = new Set(
    availableIds || DEFAULT_DASHBOARD_WIDGET_ORDER,
  );
  const seen = new Set();
  const next = [];

  if (Array.isArray(savedOrder)) {
    for (const id of savedOrder) {
      if (!allowed.has(id) || seen.has(id)) {
        continue;
      }
      if (!DASHBOARD_WIDGET_BY_ID[id]) {
        continue;
      }
      next.push(id);
      seen.add(id);
    }
  }

  for (const id of DEFAULT_DASHBOARD_WIDGET_ORDER) {
    if (!allowed.has(id) || seen.has(id)) {
      continue;
    }
    next.push(id);
    seen.add(id);
  }

  return next;
}

/**
 * Derive mobile/tablet swipe screen order from the canonical widget order.
 * KPI cards collapse to a single `kpi_dashboard` slot at the first KPI position.
 */
export function deriveCarouselOrderFromWidgetOrder(widgetOrder) {
  const screens = [];
  let kpiInserted = false;

  for (const widgetId of widgetOrder || []) {
    const widget = DASHBOARD_WIDGET_BY_ID[widgetId];
    if (!widget) {
      continue;
    }

    if (widget.kind === DASHBOARD_WIDGET_KIND.KPI) {
      if (!kpiInserted) {
        screens.push("kpi_dashboard");
        kpiInserted = true;
      }
      continue;
    }

    if (!screens.includes(widget.screenId)) {
      screens.push(widget.screenId);
    }
  }

  // Ensure every default carousel screen remains reachable.
  for (const entry of DASHBOARD_CAROUSEL_ENTRIES) {
    if (!screens.includes(entry.id)) {
      screens.push(entry.id);
    }
  }

  return screens;
}

export function getAdjacentScreenInOrder(order, current, direction) {
  const list = Array.isArray(order) && order.length > 0 ? order : [];
  const index = list.indexOf(current);
  if (index === -1 || list.length === 0) {
    return null;
  }

  const len = list.length;
  if (direction === "left") {
    return list[(index + 1) % len];
  }
  if (direction === "right") {
    return list[(index - 1 + len) % len];
  }
  return null;
}

/** KPI widget ids in canonical order (for stacking on the kpi_dashboard page). */
export function getOrderedKpiWidgetIds(widgetOrder) {
  return (widgetOrder || []).filter(
    (id) => DASHBOARD_WIDGET_BY_ID[id]?.kind === DASHBOARD_WIDGET_KIND.KPI,
  );
}
